/**
 * bb-hmenu-mobile.js — Bower & Branch
 *
 * Left slide-in panels for the horizontal (bottom-row) menu on mobile / touch.
 *
 * Why this exists: the desktop horizontal nav (`<header-menu>`) is `display: none`
 * unless the viewport is >= 750px AND `data-menu-style='menu'`
 * (sections/header.liquid:522-530), and utilities.js#setHeaderMenuStyle forces
 * `drawer` on ANY touch device (`'ontouchstart' in window &&
 * navigator.maxTouchPoints > 0`). So on phones and tablets the horizontal menu's
 * dropdowns were not merely hard to reach — they were never rendered. Touch users
 * only got the flat, horizontally scrolling `navigation_bar` row, where a parent
 * link navigated instead of revealing its children.
 *
 * Each panel is a real <dialog> opened with showModal(). That buys three things
 * this component would otherwise hand-roll, all of which are easy to get subtly
 * wrong: the focus trap, Escape-to-close, and inertness of the page behind.
 *
 * It also solves a positioning problem. `#header-component` sets `contain: layout`,
 * which makes it the containing block for `position: fixed` descendants — a panel
 * left in the header would anchor to the header box rather than the viewport. The
 * top layer is exempt from ancestor `contain` / `overflow` / `transform` / z-index,
 * so the dialog escapes without the node ever leaving the light DOM. That matters:
 * relocating it to <body> desyncs assets/morph.js, which still receives the portal
 * in the server markup and would splice an unwired duplicate back into the header
 * on the next full section render.
 *
 * Open/close animation is pure CSS (`[open]` + `@starting-style` +
 * `transition-behavior: allow-discrete`), so the exit transition runs for a native
 * Escape close too, which a JS-driven class toggle cannot intercept.
 */

import { lockScroll, unlockScroll } from '@theme/utilities';

class BBHMenuMobile extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #openPanel = null;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    // Close before teardown so the `close` handler releases the scroll lock; the
    // extra unlock is a backstop in case the panel was already closed.
    this.#openPanel?.close();
    unlockScroll(this);
  }

  /**
   * Theme-editor backstop. If a re-render removes the open panel's <dialog> while
   * this element itself survives, a `close` event on removal-from-top-layer is
   * not something every engine guarantees — so the scroll lock could never be
   * released, leaving the page unscrollable with no panel left to dismiss.
   * utilities.js's own pruning cannot help: it keys on the owner, and this
   * element is still connected.
   */
  #onSectionLoad = () => {
    if (this.#openPanel && !this.#openPanel.isConnected) {
      this.#openPanel = null;
      this.#lastTrigger = null;
      unlockScroll(this);
    }
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-bb-hmenu-close]')) {
      event.preventDefault();
      this.#openPanel?.close();
      return;
    }

    const trigger = target.closest('[data-bb-hmenu-open]');
    if (trigger instanceof HTMLElement) {
      event.preventDefault();
      this.#open(trigger);
      return;
    }

    // Backdrop click: a click on a modal <dialog>'s ::backdrop reports the dialog
    // itself as the target, so anything landing directly on the element (rather
    // than on its contents) came from outside the panel. Same detection the
    // theme already uses in bb-zip-lookup.js.
    if (target instanceof HTMLDialogElement && target === this.#openPanel) {
      target.close();
    }
  };

  /** @param {HTMLElement} trigger */
  #open(trigger) {
    const id = trigger.dataset.bbHmenuOpen;
    // Queried live rather than cached so a section morph that replaces the panels
    // cannot leave this holding a detached node.
    const panel = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!(panel instanceof HTMLDialogElement)) return;

    // Tapping the open panel's own trigger closes it.
    if (panel === this.#openPanel && panel.open) {
      panel.close();
      return;
    }

    // Retire the outgoing trigger synchronously. close() pops the dialog off the
    // top layer at once but queues its `close` EVENT as a task, so by the time
    // #onClose runs both #lastTrigger and #openPanel already point at the new
    // panel — it cannot clean up on the old one's behalf.
    if (this.#lastTrigger && this.#lastTrigger !== trigger) {
      this.#lastTrigger.setAttribute('aria-expanded', 'false');
    }
    this.#openPanel?.close();

    this.#lastTrigger = trigger;
    this.#openPanel = panel;

    // `once` keeps this correct across morphs — the listener belongs to this
    // open, not to a node we assume will still be here later.
    panel.addEventListener('close', this.#onClose, { once: true });
    panel.showModal();

    // showModal() blocks interaction but not scrolling behind the dialog on iOS.
    // Uses the theme's shared, ref-counted lock (html[scroll-lock] in base.css)
    // so this composes with cart-drawer / theme-drawer instead of fighting them.
    lockScroll(this);
    trigger.setAttribute('aria-expanded', 'true');
  }

  /** @param {Event} event */
  #onClose = (event) => {
    // Bail on a superseded panel's belated close event. #open() has already
    // re-pointed #openPanel and taken a fresh scroll lock, so releasing it here
    // would unlock the page while the NEW panel is still open and modal. The
    // outgoing trigger's aria-expanded was already reset synchronously in
    // #open(), so nothing is left undone.
    if (event.target !== this.#openPanel) return;

    this.#openPanel = null;
    unlockScroll(this);

    if (this.#lastTrigger) {
      this.#lastTrigger.setAttribute('aria-expanded', 'false');
      // Return focus to where it came from. Guarded because the trigger may have
      // been removed by a re-render while the panel was open.
      if (this.#lastTrigger.isConnected) this.#lastTrigger.focus();
      this.#lastTrigger = null;
    }
  };
}

if (!customElements.get('bb-hmenu-mobile')) {
  customElements.define('bb-hmenu-mobile', BBHMenuMobile);
}
